import qs from "query-string";

import { isGUID } from "@bps/common-utils";
import { DateTime } from "@bps/date-time";
import { AxiosInstance } from "@bps/http-client";
import {
  MIN_PAGE_ITEMS,
  PAGING_PAGE_NUMBER,
  PAGING_TOTAL_COUNT,
  PAGING_TOTAL_PAGES
} from "@libs/api/types/headers.constants";
import { PagingResponse } from "@libs/paging/paging-response.type";
import { PagingTableStorageResponse } from "@libs/paging/paging-table-storage-response.type";
import { withPaging } from "@libs/paging/paging.utils";

import {
  BpIdAuditLogArgs,
  BpIdAuditLogDto,
  BpIdGraphAuditLog,
  BpIdGraphAuditLogArgs,
  BpIdUserDto,
  BpIdUserSignInLog,
  BpIdUserSignInLogArgs,
  BpIdUsersSearchArgs,
  CloudApplicationsRefDataDto,
  InvitationsArgs,
  ResultsResponse,
  UserGroupDto,
  UserGroupSearch,
  UserInviteArgs,
  UserInviteDto,
  UserInviteResponse
} from "./bp-id.dtos";
import { IBpIdOpsGateway } from "./BpIdOpsGateway.interface";

type AuditPageWrapper<T> = {
  items: T[];
  offset: number;
  limit: number;
  total: number;
};

export class BpIdOpsGateway implements IBpIdOpsGateway {
  constructor(private api: AxiosInstance) {}

  public async getUserDetails(bpIdUserId: string): Promise<BpIdUserDto> {
    const { data } = await this.api.get(`/users/details/${bpIdUserId}`);
    return data;
  }

  public async getUserSignInLogs(
    args: BpIdUserSignInLogArgs
  ): Promise<
    PagingTableStorageResponse<BpIdUserSignInLog, BpIdUserSignInLogArgs>
  > {
    const url = qs.stringifyUrl({
      url: "users/signInLogs",
      query: {
        userId: args.userId,
        startDate: args?.startDate
          ? DateTime.fromJSDate(args?.startDate)
              .setZone("utc")
              .toISO()
          : undefined,
        endDate: args?.endDate
          ? DateTime.fromJSDate(args?.endDate)
              .endOf("day")
              .setZone("utc")
              .toISO()
          : undefined,
        pageLength: MIN_PAGE_ITEMS,
        nextPageToken: args.nextPageToken
      }
    });

    const data = (await this.api.get(url)).data;
    return { results: data.list, args: data.args };
  }

  public async getAuditLogs(
    args: BpIdGraphAuditLogArgs
  ): Promise<
    PagingTableStorageResponse<BpIdGraphAuditLog, BpIdGraphAuditLogArgs>
  > {
    const url = qs.stringifyUrl({
      url: "users/auditLogs",
      query: {
        startDate: args?.startDate
          ? DateTime.fromJSDate(args?.startDate).toISO()
          : undefined,
        endDate: args?.endDate
          ? DateTime.fromJSDate(args?.endDate)
              .endOf("day")
              .toISO()
          : undefined,
        pageLength: MIN_PAGE_ITEMS,
        nextPageToken: args.nextPageToken
      }
    });

    const data = (await this.api.get(url)).data;
    return { results: data.list, args: data.args };
  }
  public getBpIdAuditLogs = async (
    bpIdUserId: string,
    args?: BpIdAuditLogArgs
  ): Promise<PagingResponse<BpIdAuditLogDto>> => {
    const page = args?.pageParam ?? 1;
    const url = qs.stringifyUrl({
      url: `audit/user/${bpIdUserId}`,
      query: withPaging({
        page: args?.pageParam ?? 1,
        limit: args?.limit ?? MIN_PAGE_ITEMS,
        ...args,
        startDate: args?.startDate
          ? DateTime.fromJSDate(args?.startDate)
              .startOf("day")
              .setZone("utc")
              .toISO()
          : undefined,
        endDate: args?.endDate
          ? DateTime.fromJSDate(args?.endDate)
              .endOf("day")
              .setZone("utc")
              .toISO()
          : undefined
      })
    });

    const response = await this.api.get<AuditPageWrapper<BpIdAuditLogDto>>(url);

    const totalPages = +response.data.total / MIN_PAGE_ITEMS;
    const pageNumber = +response.data.offset / MIN_PAGE_ITEMS;

    const isMaxPages = pageNumber >= totalPages;

    const total = response.data.total + response.data.offset;

    const result = {
      results: response.data.items,
      next: !isMaxPages ? page + 1 : undefined,
      total,
      totalPages
    };

    return result;
  };

  public deleteUser = (bpIdUserId: string): Promise<void> => {
    return this.api.delete(`/users/${bpIdUserId}`);
  };

  public postIdentitySearch(
    values: BpIdUsersSearchArgs
  ): Promise<ResultsResponse<BpIdUserDto>> {
    if (values.text) {
      const textValue = values.text;
      values.name = undefined;
      values.objectId = undefined;
      values.email = undefined;
      if (textValue.includes("@")) {
        values.email = textValue;
      } else if (isGUID(textValue)) {
        values.objectId = textValue;
      } else {
        values.name = textValue;
      }
    }

    return this.api
      .post<ResultsResponse<BpIdUserDto>>("/users/$search", values)
      .then(({ data }) => data);
  }

  public async postAudit(id: string, text: string): Promise<any> {
    return (await this.api.post("/audit", { Id: id, text })).data;
  }

  public async getGroup(groupId: string): Promise<UserGroupDto> {
    return (await this.api.get(`/groups/${groupId}`)).data;
  }

  public async getAllGroups(): Promise<UserGroupSearch> {
    return (await this.api.get("/groups/")).data;
  }

  public async getUserGroups(userId: string): Promise<UserGroupDto[]> {
    return (await this.api.get(`/users/${userId}/groups`)).data?.groups;
  }

  public async updateUserGroups(
    userId: string,
    addGroupIds: string[],
    removeGroupIds: string[]
  ): Promise<BpIdUserDto> {
    return (
      await this.api.put(`/users/${userId}/groups`, {
        userId,
        addGroupIds,
        removeGroupIds
      })
    ).data;
  }

  public async addUserToGroup(
    userId: string,
    groupId: string
  ): Promise<UserGroupDto> {
    return (await this.api.post(`/groups/${groupId}/members/${userId}`)).data;
  }

  public async removeUserFromGroup(
    userId: string,
    groupId: string
  ): Promise<UserGroupDto> {
    return (await this.api.delete(`/groups/${groupId}/members/${userId}`)).data;
  }

  public fetchInvitations = async (
    args?: InvitationsArgs
  ): Promise<PagingResponse<UserInviteDto>> => {
    const page = args?.pageParam ?? 1;

    const encodedName = btoa(args?.name ?? "");

    const url = qs.stringifyUrl({
      url: "invites",
      query: withPaging({
        tenantId: args?.tenantId,
        userId: args?.userId,
        encodedName,
        createdStartDate: args?.createdStartDate,
        createdEndDate: args?.createdEndDate,
        expiryStartDate: args?.expiryStartDate,
        expiryEndDate: args?.expiryEndDate,
        isExpiredOrCancelled: args?.isExpired,
        isRedeemed: args?.isRedeemed,
        sortField: args?.sortField,
        sortAscending: args?.sortAscending,
        page: args?.pageParam ?? 1,
        limit: args?.limit ?? MIN_PAGE_ITEMS
      })
    });

    const response = await this.api.get<UserInviteDto[]>(url);

    const totalPages = +response.headers[PAGING_TOTAL_PAGES];
    const pageNumber = +response.headers[PAGING_PAGE_NUMBER];

    const isMaxPages = pageNumber >= totalPages;

    const total = +response.headers[PAGING_TOTAL_COUNT];

    return {
      results: response.data,
      next: !isMaxPages ? page + 1 : undefined,
      total,
      totalPages
    };
  };

  public sendUserInvite = async (
    payload: UserInviteArgs
  ): Promise<UserInviteResponse> => {
    return (await this.api.post("/invites/$sendInvite", payload)).data;
  };

  public resendUserInvite = async (id: string): Promise<void> => {
    return (await this.api.post(`/invites/${id}/$reSendInvite`)).data;
  };

  public cancelUserInvite = async (id: string): Promise<void> => {
    return (await this.api.put(`/invites/${id}/$cancel`)).data;
  };

  public getCloudApplicationsRefData = (): Promise<
    CloudApplicationsRefDataDto[]
  > => {
    return this.api.get("ref/cloudApplications").then(({ data }) => data);
  };

  public logoutAllSessions = (bpIdUserId: string): Promise<boolean> => {
    return this.api
      .get(`/users/logoutAllSessions/${bpIdUserId}`)
      .then(({ data }) => data);
  };

  public userRemoveTenant = (
    tenantId: string,
    userId: string
  ): Promise<boolean> => {
    return this.api
      .put(`/users/remove/tenant/${tenantId}/${userId}`)
      .then(({ data }) => data);
  };
  public repairFederatedUserAccounts = (
    bpIdUserId: string
  ): Promise<BpIdUserDto> => {
    return this.api
      .put(`/users/repair/federatedAccounts/${bpIdUserId}`)
      .then(({ data }) => data);
  };

  public removeMfaMethod = (
    bpIdUserId: string,
    mfaMethod: string
  ): Promise<BpIdUserDto> => {
    return this.api
      .post(`/users/removeMfaMethod/${bpIdUserId}/${mfaMethod}`)
      .then(({ data }) => data);
  };
}
